function parseHexColor(hex) {
  if (!hex.startsWith('#')) {
    hex = '#' + hex;
  }
  // #FFF
  if (hex.length === 4) {
    const r = parseInt(hex[1] + hex[1], 16);
    const g = parseInt(hex[2] + hex[2], 16);
    const b = parseInt(hex[3] + hex[3], 16);
    return { r, g, b, a: 255 };
    // #FFFFFF
  } else if (hex.length === 7) {
    const r = parseInt(hex[1] + hex[2], 16);
    const g = parseInt(hex[3] + hex[4], 16);
    const b = parseInt(hex[5] + hex[6], 16); // Fixed: was hex[7]
    const a = 255;
    return { r, g, b, a };
    // #FFFFFFFF
  } else if (hex.length === 9) {
    const r = parseInt(hex[1] + hex[2], 16); // Fixed: was hex[1] + hex[1]
    const g = parseInt(hex[3] + hex[4], 16); // Fixed: was hex[2] + hex[2]
    const b = parseInt(hex[5] + hex[6], 16); // Fixed: was hex[3] + hex[3]
    const a = parseInt(hex[7] + hex[8], 16); // Fixed: was hex[4] + hex[4]
    return { r, g, b, a };
  }
  return { r: 0, g: 0, b: 0, a: 0 };
}

function parseRGBColor(rgb) {
  const [r, g, b] = rgb
    .slice(4, -1)
    .split(',')
    .map((v) => parseInt(v.trim())); // Added trim()
  return { r, g, b, a: 255 };
}

/// rgba(255, 0, 0, 0.5)
function parseRGBAColor(rgba) {
  const parts = rgba
    .slice(5, -1)
    .split(',')
    .map((v) => v.trim());
  const r = parseInt(parts[0]);
  const g = parseInt(parts[1]);
  const b = parseInt(parts[2]);
  const a = parseFloat(parts[3]); // Use parseFloat for alpha
  return { r, g, b, a: Math.round(a * 255) };
}

// Static named color definitions for efficient lookup
export const NAMED_COLORS = {
  transparent: { r: 0, g: 0, b: 0, a: 0 },
  red: { r: 255, g: 0, b: 0, a: 255 },
  green: { r: 0, g: 255, b: 0, a: 255 },
  blue: { r: 0, g: 0, b: 255, a: 255 },
  black: { r: 0, g: 0, b: 0, a: 255 },
  white: { r: 255, g: 255, b: 255, a: 255 },
  yellow: { r: 255, g: 255, b: 0, a: 255 },
  purple: { r: 128, g: 0, b: 128, a: 255 },
  cyan: { r: 0, g: 255, b: 255, a: 255 },
  magenta: { r: 255, g: 0, b: 255, a: 255 },
  gray: { r: 128, g: 128, b: 128, a: 255 },
  maroon: { r: 128, g: 0, b: 0, a: 255 },
  olive: { r: 128, g: 128, b: 0, a: 255 },
  lime: { r: 0, g: 255, b: 0, a: 255 },
  teal: { r: 0, g: 128, b: 128, a: 255 },
  navy: { r: 0, g: 0, b: 128, a: 255 },
  silver: { r: 192, g: 192, b: 192, a: 255 },
  orange: { r: 255, g: 165, b: 0, a: 255 },
  brown: { r: 165, g: 42, b: 42, a: 255 },
  pink: { r: 255, g: 192, b: 203, a: 255 },
  gold: { r: 255, g: 215, b: 0, a: 255 },
  violet: { r: 238, g: 130, b: 238, a: 255 },
  indigo: { r: 75, g: 0, b: 130, a: 255 },
  beige: { r: 245, g: 245, b: 220, a: 255 },
  azure: { r: 240, g: 255, b: 255, a: 255 },
  lavender: { r: 230, g: 230, b: 250, a: 255 },
};

function parseNamedColor(name) {
  return NAMED_COLORS[name] || null;
}

// Convert RGBA to HSL
function rgbaToHsl(rgba) {
  // Extract RGBA components
  let { r, g, b } = rgba;

  // Normalize RGB values to 0-1 range
  r /= 255;
  g /= 255;
  b /= 255;

  // Find min and max values for RGB components
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);

  // Calculate lightness
  let h,
    s,
    l = (max + min) / 2;

  // If max equals min, it's a shade of gray (no saturation)
  if (max === min) {
    h = s = 0; // achromatic (gray)
  } else {
    // Calculate saturation
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    // Calculate hue
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }

    h /= 6;
  }

  // Convert to degrees and percentages
  h = Math.round(h * 360);
  s = Math.round(s * 100);
  l = Math.round(l * 100);

  return { h, s, l };
}

export function hslToHex(h, s, l) {
  // Normalize inputs
  h = ((h % 360) + 360) % 360; // Ensure h is between 0-360
  s = Math.max(0, Math.min(100, s)); // Clamp s between 0-100
  l = Math.max(0, Math.min(100, l)); // Clamp l between 0-100

  // Convert to 0-1 range
  h /= 360;
  s /= 100;
  l /= 100;

  let r, g, b;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = (x) => {
    const hex = Math.round(Math.max(0, Math.min(255, x * 255))).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// Parse color and convert to HSL
export function parseColorToHsl(colorStr) {
  try {
    // Handle hex, rgb, rgba, or named colors
    const rgba = parseColor(colorStr);
    if (!rgba) {
      return { h: 0, s: 100, l: 50 }; // Default red
    }
    return rgbaToHsl(rgba);
  } catch (e) {
    console.error('Error parsing color:', colorStr, e);
    return { h: 0, s: 100, l: 50 }; // Default red
  }
}

export function parseColor(color) {
  const colorLower = color.toLowerCase();
  const result = parseNamedColor(colorLower);
  if (result) {
    return result;
  }
  if (color.startsWith('rgba')) {
    return parseRGBAColor(color);
  } else if (color.startsWith('rgb')) {
    return parseRGBColor(color);
  } else if (color.startsWith('hsla')) {
    return parseHSLAColor(color);
  } else if (color.startsWith('hsl')) {
    return parseHSLColor(color);
  } else {
    return parseHexColor(color);
  }
}

function parseHSLColor(hsl) {
  // Extract values from hsl(h, s%, l%) format
  const match = hsl.match(/hsl\(\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})%\s*,\s*([0-9]{1,3})%\s*\)/i);
  if (!match) {
    return { r: 0, g: 0, b: 0, a: 255 };
  }

  const h = parseInt(match[1]);
  const s = parseInt(match[2]);
  const l = parseInt(match[3]);

  // Convert HSL to RGB using the existing hslToRGBA function
  const { r, g, b } = hslToRGBA(h, s, l);

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
    a: 255,
  };
}

function parseHSLAColor(hsla) {
  // Extract values from hsla(h, s%, l%, a) format
  const match = hsla.match(
    /hsla\(\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})%\s*,\s*([0-9]{1,3})%\s*,\s*(0|1|0?\.[0-9]+)\s*\)/i,
  );
  if (!match) {
    return { r: 0, g: 0, b: 0, a: 0 };
  }

  const h = parseInt(match[1]);
  const s = parseInt(match[2]);
  const l = parseInt(match[3]);
  const a = parseFloat(match[4]);

  // Convert HSL to RGB using the existing hslToRGBA function
  const { r, g, b } = hslToRGBA(h, s, l);

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
    a: Math.round(a * 255),
  };
}

// HSL to RGB conversion for preview
export const hslToRGBA = (h, s, l, a = 1) => {
  h /= 360;
  s /= 100;
  l /= 100;

  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  let r, g, b;

  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return { r, g, b, a };
};

function colorFromRGBA(color) {
  const { r, g, b, a } = color;
  return `rgba(${r}, ${g}, ${b}, ${a / 255})`;
}

function colorFromRGB(color) {
  const { r, g, b } = color;
  return `rgb(${r}, ${g}, ${b})`;
}

function colorFromHex(color) {
  const { r, g, b, a } = color;
  const toHex = (value) => {
    const hex = value.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return '#' + toHex(r) + toHex(g) + toHex(b) + (a !== 255 ? toHex(a) : '');
}

export function colorFormat(color, format = 'hex') {
  if (format === 'hex') {
    return colorFromHex(color);
  } else if (format === 'rgb') {
    return colorFromRGB(color);
  } else if (format === 'rgba') {
    return colorFromRGBA(color);
  }
  return color;
}

const TRANSPARENT_RGBA_REG = /rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*(0|0\.0*)\s*\)/;
const HSLA_ZERO_ALPHA_REG = /^hsla\(\s*[\d.]+\s*,\s*[\d.]+%?\s*,\s*[\d.]+%?\s*,\s*(0|0\.0*)\s*\)$/;

// Precompiled cache for common transparent values
const TRANSPARENT_VALUES = new Set([
  'transparent',
  'none',
  'rgba(0,0,0,0)',
  'rgba(0, 0, 0, 0)',
  'rgba(255,255,255,0)',
  'rgba(255, 255, 255, 0)',
  '#00000000',
  '#ffffff00',
  '#0000',
  'hsla(0,0%,0%,0)',
  'hsla(0, 0%, 0%, 0)',
]);

export function isTransparentFill(fill) {
  if (!fill) return true;

  // Quick check for common transparent values using Set lookup (O(1))
  if (TRANSPARENT_VALUES.has(fill)) return true;

  // Handle string color values
  if (typeof fill === 'string') {
    // Fast path: check for normalized common patterns first
    const len = fill.length;
    const firstChar = fill[0];

    // Handle hex colors with 0 alpha (most common cases)
    if (firstChar === '#') {
      if (len === 9 && fill.endsWith('00')) {
        // #RRGGBBAA
        return true;
      }
      if (len === 5 && fill[4] === '0') {
        // #RGBA
        return true;
      }
      return false; // Other hex formats are opaque
    }

    // Handle rgba with 0 alpha using pre-compiled regex
    if (firstChar === 'r' && len > 10) {
      // Quick filter for "rgba("
      return TRANSPARENT_RGBA_REG.test(fill);
    }

    // Handle hsla with 0 alpha using pre-compiled regex
    if (firstChar === 'h' && len > 10) {
      // Quick filter for "hsla("
      return HSLA_ZERO_ALPHA_REG.test(fill);
    }

    // Handle gradients - if all color stops are transparent, consider the fill transparent
    if (firstChar === 'l' || firstChar === 'r' || firstChar === 'c') {
      // linear/radial/conic gradient prefixes
      if (isGradient(fill)) {
        const colorStops = extractGradientColors(fill);
        if (colorStops.length > 0 && colorStops.every((color) => isTransparentFill(color))) {
          return true;
        }
      }
    }
  }

  // Handle object-based color representations (less common, check last)
  if (typeof fill === 'object' && fill !== null) {
    // Check for color objects with alpha property
    if ('a' in fill && (fill.a === 0 || fill.a === '0')) {
      return true;
    }
    // Check for alpha property as decimal
    if ('alpha' in fill && (fill.alpha === 0 || fill.alpha === '0')) {
      return true;
    }
  }

  return false;
}

export function isSemiTransparent(fill) {
  if (!fill || typeof fill !== 'string') {
    return false;
  }

  // Handle transparent keyword
  if (fill === 'transparent') {
    return true;
  }

  // Handle gradient colors
  if (isGradient(fill)) {
    // Extract color stops from gradient
    const colorStops = extractGradientColors(fill);
    // Check if any color stop is semi-transparent
    return colorStops.some((color) => isSemiTransparent(color));
  }

  // Handle rgba format
  const rgbaMatch = fill.match(/^rgba\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([\d.]+)\s*\)$/i);
  if (rgbaMatch) {
    const alpha = parseFloat(rgbaMatch[4]);
    return alpha >= 0 && alpha < 1;
  }

  // Handle hex format with alpha channel
  if (fill.startsWith('#')) {
    if (fill.length === 9) {
      // #RRGGBBAA
      const alpha = parseInt(fill.slice(7, 9), 16);
      return alpha >= 0 && alpha < 255;
    }
    if (fill.length === 5) {
      // #RGBA
      const alpha = parseInt(fill[4], 16);
      return alpha >= 0 && alpha < 15;
    }
  }

  // Handle hsla format
  const hslaMatch = fill.match(/^hsla\(\s*(\d+)\s*,\s*(\d+)%\s*,\s*(\d+)%\s*,\s*([\d.]+)\s*\)$/i);
  if (hslaMatch) {
    const alpha = parseFloat(hslaMatch[4]);
    return alpha >= 0 && alpha < 1;
  }

  return false;
}

function isGradient(fill) {
  if (!fill || typeof fill !== 'string') return false;
  const normalizedFill = fill.trim().toLowerCase();
  return (
    normalizedFill.startsWith('linear-gradient') ||
    normalizedFill.startsWith('radial-gradient') ||
    normalizedFill.startsWith('conic-gradient') ||
    normalizedFill.startsWith('repeating-linear-gradient') ||
    normalizedFill.startsWith('repeating-radial-gradient') ||
    normalizedFill.startsWith('repeating-conic-gradient')
  );
}

function extractGradientColors(gradient) {
  // Extract colors from gradient string
  const colorRegex = /(rgba?\([^)]+\)|hsla?\([^)]+\)|#[0-9a-f]{3,8}|[a-z]+)/gi;
  const matches = gradient.match(colorRegex) || [];

  // Filter out direction keywords and percentage values
  const directionKeywords = [
    'to',
    'top',
    'bottom',
    'left',
    'right',
    'center',
    'deg',
    'rad',
    'turn',
  ];
  return matches.filter((match) => {
    const normalized = match.toLowerCase();
    return (
      !directionKeywords.some((keyword) => normalized.includes(keyword)) &&
      !normalized.match(/^\d+%?$/) && // Filter out percentages and numbers
      !normalized.match(/^\d+deg$/)
    ); // Filter out degree values
  });
}
